import React, { useMemo, useState, useCallback, useEffect } from "react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import CustomSelect from "@/shared/components/ui/Select/CustomSelect";
import InlineAlert from "@/shared/components/ui/Alert/InlineAlert";
import { Loader2, RefreshCcw, X } from "lucide-react";
import { isLinkedInCompanyUrl } from "../formSchema/addContactFormSchema";

const SKIP = "";
const SKIP_SELECT_KEY = "__skip__";

export const CONTACT_IMPORT_SYSTEM_FIELDS = [
  // Original application fields (preserving exact keys!)
  { key: "fullName", label: "Full Name", required: false },
  { key: "designation", label: "Title / Designation", required: true },
  { key: "linkedinUrl", label: "Person Linkedin Url / LinkedIn URL", required: false },
  { key: "headline", label: "Headline", required: false },
  { key: "company", label: "Company / Company Name", required: false },
  { key: "companySize", label: "Employees / Company size", required: true },
  { key: "industry", label: "Industry", required: true },
  { key: "country", label: "Country", required: "location" },
  { key: "state", label: "State / Region", required: "location" },
  { key: "email", label: "Email", required: true },
  { key: "whatsappNumber", label: "WhatsApp number / Phone", required: false },
  { key: "tags", label: "Tags", required: false },

  // Additional CRM fields for separate mapping
  { key: "firstName", label: "First Name", required: false },
  { key: "lastName", label: "Last Name", required: false },
  { key: "companyNameForEmails", label: "Company Name for Emails", required: false },
  { key: "primaryEmailVerificationSource", label: "Primary Email Verification Source", required: false },
  { key: "subDepartments", label: "Sub Departments", required: false },
  { key: "workDirectPhone", label: "Work Direct Phone", required: false },
  { key: "homePhone", label: "Home Phone", required: false },
  { key: "mobilePhone", label: "Mobile Phone", required: false },
  { key: "corporatePhone", label: "Corporate Phone", required: false },
  { key: "otherPhone", label: "Other Phone", required: false },
  { key: "doNotCall", label: "Do Not Call", required: false },
  { key: "website", label: "Website", required: false },
  { key: "companyLinkedinUrl", label: "Company Linkedin Url", required: false },
  { key: "leadSource", label: "Lead Source", required: false },
  { key: "socialLeadId", label: "Social Lead ID", required: false },
  { key: "emailOptOut", label: "Email Opt Out", required: false },
  { key: "buyingRole", label: "Buying Role", required: false },
  { key: "persona", label: "Persona", required: false },
  { key: "description", label: "Description", required: false },
  { key: "city", label: "City", required: false },
  { key: "annualRevenue", label: "Annual Revenue", required: false },
  { key: "subsidiaryOf", label: "Subsidiary of (Organization ID)", required: false }
];

// Lead score needs a location; either column satisfies it.
const LOCATION_FIELD_KEYS = CONTACT_IMPORT_SYSTEM_FIELDS.filter(
  (f) => f.required === "location",
).map((f) => f.key);

export function getMissingRequiredImportFields(mapping) {
  const missing = CONTACT_IMPORT_SYSTEM_FIELDS.filter(
    (f) => f.required === true && !mapping[f.key],
  ).map((f) => f.label);
  if (!LOCATION_FIELD_KEYS.some((key) => mapping[key])) missing.push("Country or State / Region");
  return missing;
}

const SYSTEM_FIELD_ALIASES = {
  // Original keys with their common aliases
  fullName: ["fullname", "full name", "full_name", "name", "contactname", "contact name"],
  designation: ["designation", "title", "jobtitle", "job title", "position", "role"],
  linkedinUrl: ["linkedinurl", "linkedin url", "linkedin", "linkedin profile", "linkedin_profile", "personlinkedinurl", "person linkedin url", "person_linkedin_url"],
  headline: ["headline"],
  company: ["company", "companyname", "company name", "company_name", "organization", "org", "account", "firm"],
  companySize: ["companysize", "company size", "company_size", "numberemployees", "number of employees", "no of employees", "# employees", "employees", "headcount"],
  industry: ["industry", "sector", "vertical", "industries"],
  country: ["country", "nation", "country/region", "countryregion"],
  state: ["state", "region", "province", "state/province"],
  email: ["email", "emailaddress", "email address", "emailid", "email_id", "mail"],
  whatsappNumber: ["whatsapp", "whatsappnumber", "whatsapp number", "phone", "telephone", "phone number", "phonenumber"],
  tags: ["tags", "labels", "tag", "label"],

  // Additional keys with their aliases
  firstName: ["firstname", "first name", "first_name", "first", "fname", "givenname", "given name"],
  lastName: ["lastname", "last name", "last_name", "last", "lname", "surname"],
  companyNameForEmails: ["companynameforemails", "company name for emails", "emailcompanyname", "email company name"],
  primaryEmailVerificationSource: ["primaryemailverificationsource", "primary email verification source", "emailverificationsource", "verification source"],

  website: ["website", "websiteurl", "website url", "web", "domain", "company website"],
  companyLinkedinUrl: ["companylinkedinurl", "company linkedin url", "company_linkedin_url", "company linkedin", "company_linkedin"],
  leadSource: ["leadsource", "lead source", "lead_source", "source"],
  socialLeadId: ["socialleadid", "social lead id", "social_lead_id"],
  emailOptOut: ["emailoptout", "email opt out", "email_opt_out", "donotemail", "do not email"],
  buyingRole: ["buyingrole", "buying role", "buying_role"],
  persona: ["persona", "contact persona"],
  description: ["description", "contact description", "notes"],
  city: ["city", "town", "locality"],
  annualRevenue: ["annualrevenue", "annual revenue", "annual_revenue", "revenue", "sales", "turnover"],
};

function normalizeCsvKey(key) {
  return String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function buildAutoMapping(headers) {
  const mapping = {};
  const usedCsvHeaders = new Set();

  for (const field of CONTACT_IMPORT_SYSTEM_FIELDS) {
    const fieldKey = field.key;
    const aliases = SYSTEM_FIELD_ALIASES[fieldKey] || [normalizeCsvKey(field.label)];

    // Find first matching CSV header that has not been mapped yet
    const matchedHeader = headers.find(h => {
      const normalizedH = normalizeCsvKey(h);
      return aliases.includes(normalizedH) && !usedCsvHeaders.has(normalizedH);
    });

    if (matchedHeader) {
      mapping[fieldKey] = matchedHeader;
      usedCsvHeaders.add(normalizeCsvKey(matchedHeader));
    } else {
      mapping[fieldKey] = SKIP;
    }
  }
  return mapping;
}

function formatSample(sampleRows, header) {
  if (!header || header === SKIP || !sampleRows?.length) return "—";
  const parts = sampleRows
    .map((row) => row[header] ?? "")
    .filter((v) => String(v).trim() !== "");
  if (!parts.length) return "—";
  return parts.slice(0, 2).join(" · ");
}

// Empty values here don't block import, but the campaign step that needs them is skipped for that contact.
const STEP_FIELD_LABELS = { linkedinUrl: "LinkedIn URL", whatsappNumber: "phone number" };

export default function FieldMappingModal({
  isOpen,
  onClose,
  headers = [],
  sampleRows = [],
  onConfirmImport,
  onCheckImport,
  isSubmitting = false,
}) {
  const [systemToCsv, setSystemToCsv] = useState({});
  // { columnMapping, missing: { linkedinUrl: n, ... } } once the file was checked and some rows lack step fields.
  const [missingCheck, setMissingCheck] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => setMissingCheck(null), [systemToCsv, isOpen]);

  useEffect(() => {
    if (isOpen && headers.length) {
      setSystemToCsv(buildAutoMapping(headers));
    }
  }, [isOpen, headers]);

  const missingRequiredFields = useMemo(
    () => getMissingRequiredImportFields(systemToCsv),
    [systemToCsv],
  );

  const linkedinSampleHasCompanyUrl = useMemo(() => {
    const csvHeader = systemToCsv.linkedinUrl;
    return Boolean(
      csvHeader &&
        sampleRows.some((row) => isLinkedInCompanyUrl(row[csvHeader])),
    );
  }, [sampleRows, systemToCsv.linkedinUrl]);

  const getMappingOptions = useCallback(
    (systemKey) => [
      { key: SKIP_SELECT_KEY, title: "— Skip —" },
      ...headers.map((h) => {
        const isTakenByOther = Object.entries(systemToCsv).some(
          ([sysKey, csvHeader]) => sysKey !== systemKey && csvHeader === h
        );
        return {
          key: h,
          title: isTakenByOther ? `${h} (used)` : h,
          disabled: isTakenByOther,
        };
      }),
    ],
    [headers, systemToCsv],
  );

  const setMappingForSystemKey = useCallback((systemKey, csvHeader) => {
    setSystemToCsv((prev) => {
      const next = { ...prev, [systemKey]: csvHeader || SKIP };
      if (csvHeader) {
        for (const [s, h] of Object.entries(next)) {
          if (s !== systemKey && h === csvHeader) next[s] = SKIP;
        }
      }
      return next;
    });
  }, []);

  const applyAutoMapping = useCallback(() => {
    setSystemToCsv(buildAutoMapping(headers));
  }, [headers]);

  const resetMapping = useCallback(() => {
    const empty = {};
    CONTACT_IMPORT_SYSTEM_FIELDS.forEach((f) => {
      empty[f.key] = SKIP;
    });
    setSystemToCsv(empty);
  }, []);

  const handleConfirm = useCallback(async () => {
    const columnMapping = {};
    Object.entries(systemToCsv).forEach(([sysKey, csvHeader]) => {
      if (csvHeader && csvHeader !== SKIP) {
        columnMapping[sysKey] = csvHeader;
      }
    });
    if (getMissingRequiredImportFields(columnMapping).length > 0) {
      return;
    }
    if (onCheckImport) {
      setIsChecking(true);
      const check = await onCheckImport(columnMapping);
      setIsChecking(false);
      if (!check) return;
      const missing = Object.fromEntries(
        Object.keys(STEP_FIELD_LABELS)
          .filter((field) => check.missing?.[field] > 0)
          .map((field) => [field, check.missing[field]]),
      );
      if (Object.keys(missing).length) {
        setMissingCheck({ columnMapping, missing, rows: Array.isArray(check.missingRows) ? check.missingRows : [] });
        return;
      }
    }
    await onConfirmImport(columnMapping);
  }, [systemToCsv, onConfirmImport, onCheckImport]);

  if (!isOpen) return null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      width="min(920px, 95vw)"
      className="p-6"
    >
      <div className="flex flex-col gap-4 h-[calc(90dvh-80px)]">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Map import fields
            </h2>
            <p className="text-xs text-gray-500 ">
              Match each contact field to a column from your file. Fields marked
              with * are required for import (they drive the lead score; rows
              with these cells empty are skipped). Unmapped fields are skipped.
              Person LinkedIn URLs must use linkedin.com/in/; company URLs are
              skipped.
            </p>
          </div>
          <button
            className="p-1 hover:bg-gray-100 rounded-full"
            onClick={onClose}
          >
            <X size={20} color="#181D27" />
          </button>
        </div>

        {missingRequiredFields.length > 0 && (
          <div
            className="rounded-lg border border-error-500 bg-error-50 px-3 py-2 text-xs text-error-500"
            role="status"
          >
            Map a column for each required field:{" "}
            {missingRequiredFields.join(", ")}
            .
          </div>
        )}

        {linkedinSampleHasCompanyUrl && (
          <div
            className="rounded-lg border border-warning-500 bg-warning-50 px-3 py-2 text-xs text-warning-700"
            role="status"
          >
            The mapped Person LinkedIn URL column contains a company URL in the
            preview. Rows with company URLs will be skipped during import.
          </div>
        )}

        <div className="flex gap-2 flex-wrap justify-end">
          <SecondaryButton
            title="Apply auto mapping"
            onClick={applyAutoMapping}
            className="shadow-none text-xs"
          />
          <SecondaryButton
            title="Reset mapping"
            icon={<RefreshCcw size={16} className="text-secondary" />}
            onClick={resetMapping}
            className="shadow-none text-xs text-secondary border-none"
          />
        </div>

        <div className="overflow-auto flex-1 min-h-0 border border-gray-100 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">
                  Application Field
                </th>
                <th className="text-left px-3 py-2 font-medium text-gray-600 w-[220px]">
                  Maps to CSV Column
                </th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">
                  Sample data
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {CONTACT_IMPORT_SYSTEM_FIELDS.map((field) => (
                <tr key={field.key} className="bg-white">
                  <td className="px-3 py-2 text-gray-800 align-middle">
                    {field.label}
                    {field.required ? " *" : ""}
                  </td>
                  <td className="px-3 py-2 align-middle min-w-[200px]">
                    <CustomSelect
                      options={getMappingOptions(field.key)}
                      selectedKeys={
                        new Set([
                          systemToCsv[field.key]
                            ? systemToCsv[field.key]
                            : SKIP_SELECT_KEY,
                        ])
                      }
                      onSelectionChange={(keys) => {
                        const raw = Array.from(keys)[0];
                        setMappingForSystemKey(
                          field.key,
                          raw === SKIP_SELECT_KEY ? SKIP : raw,
                        );
                      }}
                      placeholder="— Skip —"
                      showDescription={false}
                      size="sm"
                      variant="bordered"
                      containerClassName="gap-0"
                      triggerClassName="!min-h-9 !py-1.5 text-xs border-gray-200 shadow-none"
                      valueClassName="!text-xs"
                      selectItemClassName="!text-xs"
                    />
                  </td>
                  <td
                    className="px-3 py-2 text-xs text-gray-500 align-middle max-w-[280px] truncate"
                    title={formatSample(sampleRows, systemToCsv[field.key])}
                  >
                    {formatSample(sampleRows, systemToCsv[field.key])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {missingCheck && (
          <InlineAlert title="Some contacts are missing details">
            <ul className="list-disc pl-4">
              {Object.entries(missingCheck.missing).map(([field, count]) => (
                <li key={field}>
                  {count} contact{count !== 1 ? "s have" : " has"} no {STEP_FIELD_LABELS[field]}.
                  {field === "linkedinUrl" ? " LinkedIn steps" : " WhatsApp and call steps"} will be skipped for{" "}
                  {count !== 1 ? "them" : "this contact"}.
                </li>
              ))}
            </ul>
            {missingCheck.rows.length > 0 && (
              <ul className="mt-2 max-h-32 overflow-y-auto rounded-md border border-gray-200 bg-white px-2 py-1 dark:border-gray-700 dark:bg-gray-900">
                {missingCheck.rows.map((row) => (
                  <li key={row.rowNumber} className="flex justify-between gap-3 py-0.5">
                    <span className="truncate">
                      Row {row.rowNumber}
                      {row.name ? ` · ${row.name}` : ""}
                    </span>
                    <span className="shrink-0 text-gray-500 dark:text-gray-400">
                      No {row.missing.map((field) => STEP_FIELD_LABELS[field]).join(" or ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2">Skip these contacts, or import them anyway?</p>
          </InlineAlert>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton title="Cancel" onClick={onClose} />
          {missingCheck && (
            <SecondaryButton
              title="Skip these contacts"
              disabled={isSubmitting}
              onClick={() => onConfirmImport(missingCheck.columnMapping, Object.keys(missingCheck.missing))}
            />
          )}
          <PrimaryButton
            endContent={
              isSubmitting || isChecking ? (
                <Loader2 className="animate-spin" size={16} />
              ) : null
            }
            startContent={false}
            disabled={isSubmitting || isChecking || missingRequiredFields.length > 0}
            onClick={missingCheck ? () => onConfirmImport(missingCheck.columnMapping) : handleConfirm}
          >
            {isSubmitting ? "Importing…" : isChecking ? "Checking…" : missingCheck ? "Import anyway" : "Import"}
          </PrimaryButton>
        </div>
      </div>
    </CustomModal>
  );
}
